import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  WhatsApp as WhatsAppIcon,
  PersonOutline as PersonIcon,
  Block as BlockIcon,
  LockOpen as LockOpenIcon,
} from '@mui/icons-material';
import { useAppWindow } from './useAppWindow';
import { api, ShareLink, ShareLinkLead } from '../../services/api';
import { formatDateTime, formatRelative, SHARE_LINK_TYPE_LABELS } from './shareLinkFormat';
import { logSharableLinkAction } from '../../services/analytics';

interface Props {
  link: ShareLink | null;
  onClose: () => void;
}

/** Superscript on a lead whose mobile is listed as a property agent (Huttons user or CEA register). */
function PotentialAgentMark({ huttons }: { huttons: boolean }) {
  return (
    <Box
      component="sup"
      sx={{
        ml: 0.5, fontSize: 10, fontWeight: 700, letterSpacing: 0.3, lineHeight: 1,
        color: 'var(--color-accent)', whiteSpace: 'nowrap', verticalAlign: 'super',
      }}
    >
      {huttons ? 'Potential Agent' : 'Potential Agent (ECB)'}
    </Box>
  );
}

/** Tag on a lead the agent has shut out of this link. */
function AccessDeniedMark() {
  return (
    <Box component="span" sx={{
      ml: 1, px: 0.75, py: 0.25, borderRadius: 1, fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
      textTransform: 'uppercase', whiteSpace: 'nowrap', verticalAlign: 'middle',
      color: 'var(--color-danger)', border: '1px solid currentColor',
    }}>
      Access denied
    </Box>
  );
}

export function ShareLinkLeadsDrawer({ link, onClose }: Props) {
  const isMobile = useMediaQuery('(max-width:600px)');
  const appWindow = useAppWindow();
  const [leads, setLeads] = useState<ShareLinkLead[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [denying, setDenying] = useState<ShareLinkLead | null>(null);
  const [busyLeadId, setBusyLeadId] = useState<number | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);

  useEffect(() => {
    if (!link) return;
    let cancelled = false;
    setLeads(null);
    setError(null);
    setAccessError(null);
    api.getShareLinkLeads(link.id)
      .then((res) => { if (!cancelled) setLeads(res.leads); })
      .catch((err: Error) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [link]);

  const setAccess = async (lead: ShareLinkLead, denied: boolean) => {
    if (!link) return;
    setBusyLeadId(lead.id);
    setAccessError(null);
    try {
      const res = await api.setShareLinkLeadAccess(link.id, lead.id, denied);
      setLeads((prev) => prev?.map((l) => (l.id === lead.id ? { ...l, accessDeniedAt: res.accessDeniedAt } : l)) ?? prev);
      logSharableLinkAction({ action: denied ? 'lead_deny_access' : 'lead_restore_access', link, source: 'leads_drawer' });
      setDenying(null);
    } catch (err) {
      setAccessError((err as Error).message);
    } finally {
      setBusyLeadId(null);
    }
  };

  // Popup window in a browser, in-page panel in the app; the leads list stays open behind it.
  const openInConnector = (clientId: string | null) => {
    const path = clientId ? `/super-connector/clients/${encodeURIComponent(clientId)}` : '/super-connector';
    appWindow.open('Super Connector', `super-connector-${clientId ?? 'home'}`, path);
  };

  return (
    <>
    {appWindow.element}
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={!!link}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 420, maxHeight: isMobile ? '88vh' : '100%',
          bgcolor: 'var(--color-surface)', backgroundImage: 'none',
          borderTopLeftRadius: isMobile ? 16 : 0, borderTopRightRadius: isMobile ? 16 : 0,
        },
      }}
    >
      {link && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ p: 2.5, borderBottom: '1px solid rgba(var(--color-text-rgb), 0.1)', position: 'relative' }}>
            <Typography sx={{ fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--color-accent)', fontWeight: 700 }}>
              {SHARE_LINK_TYPE_LABELS[link.linkType]} · Leads
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-strong)', pr: 5 }}>
              {link.projectName}
            </Typography>
            {link.description && (
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mt: 0.5 }}>{link.description}</Typography>
            )}
            <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', top: 12, right: 12 }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {!leads && !error && <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} /></Box>}
            {error && <Alert severity="error">{error}</Alert>}
            {accessError && !denying && <Alert severity="error" onClose={() => setAccessError(null)}>{accessError}</Alert>}
            {leads && leads.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                <PersonIcon sx={{ fontSize: 40, color: 'var(--color-text-muted)' }} />
                <Typography sx={{ color: 'var(--color-text-strong)', fontWeight: 600, mt: 1 }}>No leads yet</Typography>
                <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mt: 0.5 }}>
                  When someone verifies their mobile on this link they'll appear here and in your Super Connector.
                </Typography>
              </Box>
            )}
            {leads?.map((lead) => (
              <Box key={lead.id} sx={{
                p: 1.75, borderRadius: 2, border: '1px solid rgba(var(--color-text-rgb), 0.1)',
                bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
                opacity: lead.accessDeniedAt ? 0.75 : 1,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 700, color: 'var(--color-text-strong)', fontVariantNumeric: 'tabular-nums' }}>
                    {lead.mobile}
                    {lead.possibleAgent && <PotentialAgentMark huttons={lead.possibleAgent.source === 'HUTTONS'} />}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                    {formatRelative(lead.lastSeenAt)}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }} component="div">
                  Verified {formatDateTime(lead.verifiedAt)} · {lead.viewCount} {lead.viewCount === 1 ? 'view' : 'views'}
                  {lead.accessDeniedAt && <AccessDeniedMark />}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
                  <Button size="small" variant="contained" endIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={() => {
                      if (link) logSharableLinkAction({ action: 'lead_open_connector', link, source: 'leads_drawer' });
                      openInConnector(lead.connectorClientId);
                    }}
                    sx={{ bgcolor: 'var(--color-accent)', textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    Open in Super Connector
                  </Button>
                  <IconButton size="small" aria-label={`WhatsApp ${lead.mobile}`}
                    href={`https://wa.me/${lead.mobile.replace(/\D/g, '')}`} target="_blank" rel="noopener"
                    onClick={() => { if (link) logSharableLinkAction({ action: 'lead_whatsapp', link, source: 'leads_drawer' }); }}
                    sx={{ color: '#25d366', border: '1px solid rgba(37, 211, 102, 0.4)' }}>
                    <WhatsAppIcon fontSize="small" />
                  </IconButton>
                  {lead.accessDeniedAt ? (
                    <Button size="small" variant="outlined" disabled={busyLeadId === lead.id}
                      aria-label={`Restore access for ${lead.mobile}`}
                      startIcon={busyLeadId === lead.id ? <CircularProgress size={14} color="inherit" /> : <LockOpenIcon fontSize="small" />}
                      onClick={() => { void setAccess(lead, false); }}
                      sx={{
                        ml: 'auto', minWidth: 0, whiteSpace: 'nowrap', textTransform: 'none', fontWeight: 600,
                        color: 'var(--color-text-strong)', borderColor: 'rgb(var(--color-text-rgb) / 0.3)',
                      }}>
                      Restore
                    </Button>
                  ) : (
                    <Button size="small" variant="outlined" disabled={busyLeadId === lead.id}
                      aria-label={`Deny ${lead.mobile} access`}
                      startIcon={<BlockIcon fontSize="small" />}
                      onClick={() => { setAccessError(null); setDenying(lead); }}
                      sx={{
                        ml: 'auto', minWidth: 0, whiteSpace: 'nowrap', textTransform: 'none', fontWeight: 600,
                        color: 'var(--color-danger)', borderColor: 'rgb(var(--color-danger-rgb) / 0.45)',
                        '&:hover': { borderColor: 'var(--color-danger)', bgcolor: 'rgb(var(--color-danger-rgb) / 0.08)' },
                      }}>
                      Deny
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Drawer>

    <Dialog open={!!denying} onClose={busyLeadId != null ? undefined : () => setDenying(null)}>
      <DialogTitle>Deny {denying?.mobile} access?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          They will no longer be able to open this link, even by verifying their mobile again. They stay in your
          leads and in Super Connector, and your other links still open for them. You can restore access any time.
        </DialogContentText>
        {accessError && <Alert severity="error" sx={{ mt: 1.5 }}>{accessError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDenying(null)} disabled={busyLeadId != null}>Cancel</Button>
        <Button color="error" variant="contained" disabled={busyLeadId != null}
          onClick={() => { if (denying) void setAccess(denying, true); }}
          startIcon={busyLeadId != null ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Deny access
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
